import {
  Button,
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import Dropdown, {
  DropdownEventEnum,
} from "@booking/components/compounds/forms/Dropdown";
import Fieldset from "@booking/components/containers/forms/Fieldset";
import { IconEnum } from "@booking/enums";
import { useSubscribe } from "@booking/hooks/usePubSub";
import { DEPARTURE_AIRPORTS } from "@sitewide/constants/enquiryData";
import React, { useState } from "react";

import DatePicker from "@/components/compounds/forminputs/DatePicker";
import { ButtonActionEnum, ColorEnum, SizeEnum } from "@/enums";
import { type PubSubData } from "@/types";

import {
  CALL_TO_ACTION_TEXT,
  MAX_GROUP_SIZE,
  MAX_NIGHTS,
  MAX_ROUNDS,
  PHONE_LINK,
  PHONE_NUMBER_DISPLAY,
} from "../utils/constants";
import { getTomorrowDate } from "../utils/dateUtils";
import { createNumberOptions, createUpdateControl } from "../utils/formHelpers";
import styles from "./CreateYourOwnForm.module.css";

interface CreateYourOwnTravelFormProps {
  courseNames?: string[];
  initialData?: Partial<CreateYourOwnTravelFormData>;
  isEu?: boolean;
  onContinue: (travelDetails: CreateYourOwnTravelFormData) => void;
}

export interface CreateYourOwnTravelFormData {
  arrival_date: string;
  courses?: Record<string, number> | null;
  flight_options?: string;
  group_size: string;
  include_flight_options?: boolean;
  nights: string;
  rounds: string;
}

const initialFormData: CreateYourOwnTravelFormData = {
  arrival_date: "",
  group_size: "1",
  include_flight_options: false,
  nights: "1",
  rounds: "0",
};

const departureAirportOptions = DEPARTURE_AIRPORTS.map((option) => ({
  text: option.text,
  value: option.value,
}));
const groupSizeOptions = createNumberOptions(1, MAX_GROUP_SIZE);
const nightsOptions = createNumberOptions(1, MAX_NIGHTS);
const roundsOptions = createNumberOptions(0, MAX_ROUNDS);

// Input field names
const INPUTS = {
  ARRIVAL_DATE: "arrival_date",
  FLIGHT_OPTIONS: "flight_options",
  GROUP_SIZE: "group_size",
  NIGHTS: "nights",
  ROUNDS: "rounds",
};

function CreateYourOwnTravelForm(props: CreateYourOwnTravelFormProps) {
  const {
    courseNames = [],
    initialData = {},
    isEu = false,
    onContinue,
  } = props;

  // Initialize course rounds state - default to 0 for each course
  const initialCourseRounds: Record<string, number> = {};
  courseNames.forEach((courseName) => {
    initialCourseRounds[courseName] = initialData.courses?.[courseName] || 0;
  });

  const [courseRounds, setCourseRounds] =
    useState<Record<string, number>>(initialCourseRounds);

  const [formData, setFormData] = useState<CreateYourOwnTravelFormData>({
    ...initialFormData,
    ...initialData,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateForm = (data: Partial<CreateYourOwnTravelFormData>) => {
    // Clear flight_options when checkbox is unchecked
    if (data.include_flight_options === false) {
      setFormData((prev) => ({
        ...prev,
        ...data,
        flight_options: "",
      }));
      // Clear flight_options error when checkbox is unchecked
      if (errors.flight_options) {
        const newErrors = { ...errors };

        delete newErrors.flight_options;
        setErrors(newErrors);
      }
    } else {
      setFormData((prev) => ({ ...prev, ...data }));
    }

    // Clear errors for updated fields
    const updatedFields = Object.keys(data);

    if (updatedFields.some((field) => errors[field])) {
      const newErrors = { ...errors };

      updatedFields.forEach((field) => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      setErrors(newErrors);
    }
  };

  const updateControl =
    createUpdateControl<CreateYourOwnTravelFormData>(updateForm);

  // Calculate total rounds from course selections
  const calculateTotalRounds = (rounds: Record<string, number>): number => {
    return Object.values(rounds).reduce((sum, value) => sum + value, 0);
  };

  // Handle course rounds change
  const handleCourseRoundsChange = (courseName: string, value: string) => {
    const newRounds = { ...courseRounds, [courseName]: parseInt(value, 10) };
    setCourseRounds(newRounds);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate flight options
    const newErrors: Record<string, string> = {};

    if (!formData.arrival_date) {
      newErrors.arrival_date = "Please select a date for your golf trip";
    }

    if (formData.include_flight_options && !formData.flight_options) {
      newErrors.flight_options = "Please select a departure airport";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // When there are courses, calculate total rounds from course selections
    // When there are no courses, use the rounds field from formData
    const totalRounds =
      courseNames.length > 0
        ? calculateTotalRounds(courseRounds)
        : parseInt(formData.rounds, 10);

    // Build courses object - include all courses with their selected values (including 0)
    // If no courses, set to null instead of empty object
    const coursesData: Record<string, number> | null =
      courseNames.length > 0 ? { ...courseRounds } : null;

    // Update formData with calculated rounds and courses
    const submitData: CreateYourOwnTravelFormData = {
      ...formData,
      rounds: totalRounds.toString(),
      courses: coursesData,
    };

    onContinue(submitData);
  };

  // Subscribe to form events
  useSubscribe(DropdownEventEnum.CHANGE, updateControl, [
    INPUTS.GROUP_SIZE,
    INPUTS.NIGHTS,
    INPUTS.ROUNDS, // Subscribe to rounds dropdown (for venues with no courses)
    INPUTS.FLIGHT_OPTIONS, // Subscribe to departure airport dropdown
  ]);

  // Subscribe to course rounds changes
  const handleCourseDropdownChange = (
    data: PubSubData & { context: string },
  ) => {
    if (data.context.startsWith("course_")) {
      const courseName = data.context.replace("course_", "");
      handleCourseRoundsChange(courseName, data.value);
    }
  };

  useSubscribe(
    DropdownEventEnum.CHANGE,
    handleCourseDropdownChange,
    courseNames.map((name) => `course_${name}`),
  );

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formInner}>
        <div>
          <DatePicker
            id={INPUTS.ARRIVAL_DATE}
            invalid={!!errors.arrival_date}
            label="Arriving On"
            labelInside
            min={getTomorrowDate()}
            name={INPUTS.ARRIVAL_DATE}
            onChange={(e) =>
              updateForm({ [INPUTS.ARRIVAL_DATE]: e.target.value })
            }
          />
          {errors.arrival_date && (
            <div className={styles.errorMessage}>{errors.arrival_date}</div>
          )}
        </div>

        <Dropdown
          icon={IconEnum.USER}
          label="Group size"
          labelInside
          name={INPUTS.GROUP_SIZE}
          options={groupSizeOptions}
          value={formData.group_size}
        />

        <Dropdown
          icon={IconEnum.MOON}
          label="Number of nights"
          labelInside
          name={INPUTS.NIGHTS}
          options={nightsOptions}
          value={formData.nights}
        />

        {courseNames.length > 0 ? (
          <Fieldset
            legend="Number of rounds"
            legendBackground={false}
            noBorder
            noPadding
          >
            <div className={styles.coursesContainer}>
              {courseNames.map((courseName) => (
                <Dropdown
                  key={courseName}
                  icon={IconEnum.GOLF_BALL_TEE}
                  label={courseName}
                  labelInside
                  name={`course_${courseName}`}
                  options={roundsOptions}
                  value={courseRounds[courseName]?.toString() || "0"}
                />
              ))}
            </div>
          </Fieldset>
        ) : (
          <Dropdown
            icon={IconEnum.GOLF_BALL_TEE}
            label="Number of rounds"
            labelInside
            name={INPUTS.ROUNDS}
            options={roundsOptions}
            value={formData.rounds}
          />
        )}

        {isEu && (
          <div>
            <label className={styles.checkboxLabel}>
              <input
                checked={formData.include_flight_options || false}
                className={styles.checkbox}
                name="include_flight_options"
                type="checkbox"
                onChange={(e) =>
                  updateForm({ include_flight_options: e.target.checked })
                }
              />
              <span>Yes, I'm interested in flight options</span>
            </label>
          </div>
        )}

        {isEu && formData.include_flight_options && (
          <div>
            <Dropdown
              icon={IconEnum.CHECK}
              invalid={!!errors.flight_options}
              label="Departure airport"
              labelInside
              name={INPUTS.FLIGHT_OPTIONS}
              options={departureAirportOptions}
              value={formData.flight_options || ""}
            />
            {errors.flight_options && (
              <div className={styles.errorMessage}>{errors.flight_options}</div>
            )}
          </div>
        )}
      </div>

      <Button
        color={ColorEnum.DARKGREEN}
        size={SizeEnum.LARGE}
        type={ButtonActionEnum.SUBMIT}
        variant={ButtonVariantEnum.SOLID}
      >
        Make an enquiry
      </Button>

      <div className={styles.callInfo}>
        <p>
          {CALL_TO_ACTION_TEXT} <a href={PHONE_LINK}>{PHONE_NUMBER_DISPLAY}</a>{" "}
          from 8:30AM today
        </p>
      </div>
    </form>
  );
}

export default CreateYourOwnTravelForm;
export { CreateYourOwnTravelForm, type CreateYourOwnTravelFormProps };
